import { faEye, faEyeSlash, faUser, faExclamationCircle, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/base/Button";
import AuthSocialButtons from "components/common/AuthSocialButtons";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "providers/AuthProvider";
import { useTranslation } from "react-i18next";
import "./SignInForm.css";

const SignInForm = ({ layout }: { layout: "simple" | "card" | "split" }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Start with untouched fields
  const [touched, setTouched] = useState({ email: false, password: false });
  const [apiError, setApiError] = useState<string | null>(null);
  const [loginFailed, setLoginFailed] = useState(false); // Track if login has failed for red borders
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, loginLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Form validation state
  const [validation, setValidation] = useState({
    email: { isValid: false, message: "" },
    password: { isValid: false, message: "" }
  });

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Update touched state based on input content
  useEffect(() => {
    // Set touched to true if field is not empty
    if (email !== '') {
      setTouched(prev => ({ ...prev, email: true }));
    }
  }, [email]);

  useEffect(() => {
    // Set touched to true if field is not empty
    if (password !== '') {
      setTouched(prev => ({ ...prev, password: true }));
    }
  }, [password]);

  // Validate email
  useEffect(() => {
    if (!touched.email) return;
    
    if (!email) {
      setValidation(prev => ({ 
        ...prev, 
        email: { isValid: false, message: t("emailRequired") || "Email is required" } 
      }));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setValidation(prev => ({ 
        ...prev, 
        email: { isValid: false, message: t("invalidEmail") || "Invalid email format" } 
      }));
    } else {
      setValidation(prev => ({ 
        ...prev, 
        email: { isValid: true, message: "" } 
      }));
    }
  }, [email, touched.email, t]);

  // Validate password
  useEffect(() => {
    if (!touched.password) return;
    
    if (!password) {
      setValidation(prev => ({ 
        ...prev, 
        password: { isValid: false, message: t("passwordRequired") || "Password is required" } 
      }));
    } else if (password.length < 6) {
      setValidation(prev => ({ 
        ...prev, 
        password: { isValid: false, message: t("passwordTooShort") || "Password is too short" } 
      }));
    } else {
      setValidation(prev => ({ 
        ...prev, 
        password: { isValid: true, message: "" } 
      }));
    }
  }, [password, touched.password, t]);

  // Check if the form is valid
  const isFormValid = email.length > 0 && password.length >= 6 && 
                      validation.email.isValid && validation.password.isValid;

  // Use refs to maintain state across re-renders
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting || loginLoading) {
      return;
    }
    
    // Mark form as submitting
    setIsSubmitting(true);
    
    // Mark all fields as touched when form is submitted
    setTouched({ email: true, password: true });
    
    // Reset login failed state when submitting
    setLoginFailed(false);
    
    // Only clear API error when form is submitted again
    setApiError(null);
    
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    
    // Check if form is valid
    if (!isFormValid) {
      setIsSubmitting(false);
      return;
    }
    

    try {
      console.log("Attempting login with credentials");
      // Login user through the AuthProvider
      await login(email, password);
      
      // Get seller status and redirect accordingly
      const seller = JSON.parse(localStorage.getItem("seller") || "{}");
      const sellerStatus = seller.is_seller;
      
      if (sellerStatus === "0") {
        // Pending sellers go to profile page to complete verification
        navigate('/profile');
      } else if (sellerStatus === "1") {
        // Approved sellers go to dashboard
        navigate('/');
      } else {
        // Default fallback - go to profile
        navigate('/profile');
      }
    } catch (err: any) {
      console.error("Login error caught in form:", err);
      
      // Display the specific error message from the GraphQL mutation
      const errorMessage = err.message || t("loginFailed");
      console.log("Setting API error message:", errorMessage);
      
      // Set API error state
      setApiError(errorMessage);
      
      // Mark login as failed to show red borders
      setLoginFailed(true);
      
      // Reset form submission state
      setIsSubmitting(false);
    }
  };

  // For debugging
  useEffect(() => {
    console.log("API Error message set:", apiError);
  }, [apiError]);

  // Helper function to get input state class
  const getInputStateClass = (field: 'email' | 'password') => {
    // If login failed or there's an API error, show red borders regardless of validation
    if (loginFailed || apiError) return "is-invalid";
    
    if (!touched[field]) return "";
    // Don't add is-valid class (no green borders)
    if (!validation[field].isValid) return "is-invalid";
    return "";
  };

  // Reset login failed state when inputs change
  useEffect(() => {
    if (loginFailed) {
      setLoginFailed(false);
    }
  }, [email, password, loginFailed]);

  // Monitor login state changes
  useEffect(() => {
    console.log('Login loading state:', loginLoading, 'Local submitting:', isSubmitting);
    
    // Reset local submission state when login completes
    if (!loginLoading && isSubmitting) {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 500);
    }
  }, [loginLoading, isSubmitting]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="wamia-sign-in-form">
      <div className="text-center mb-7">
        <h3 className="text-body-highlight">{t("signIn")}</h3>
        <p className="text-body-tertiary">{t("getAccessToYourAccount")}</p>
      </div>
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="email">{t("emailAddress")}</Form.Label>
          <div className="form-icon-container">
            <Form.Control
              id="email"
              type="email"
              className={`form-icon-input ${getInputStateClass('email')}`}
              placeholder={t("emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(prev => ({ ...prev, email: true }))}
              disabled={isSubmitting || loginLoading}
            />
            <FontAwesomeIcon
              icon={faUser}
              className="text-body fs-9 form-icon"
            />
            {/* Removed green checkmark */}
            {touched.email && !validation.email.isValid && validation.email.message && !loginFailed && !apiError && (
              <div className="invalid-feedback d-block">{validation.email.message}</div>
            )}
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="password">{t("password")}</Form.Label>
          <div className="form-icon-container">
            <Form.Control
              id="password"
              type={showPassword ? "text" : "password"}
              className={`form-icon-input ${getInputStateClass('password')}`}
              placeholder={t("passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
              disabled={isSubmitting || loginLoading}
            />
            <FontAwesomeIcon
              icon={showPassword ? faEyeSlash : faEye}
              className="text-body fs-9 form-icon"
              onClick={togglePasswordVisibility}
              style={{ 
                cursor: 'pointer',
                zIndex: 10
              }}
              title={showPassword ? "Hide password" : "Show password"}
            />
            {/* Removed green checkmark */}
            {touched.password && !validation.password.isValid && validation.password.message && !loginFailed && !apiError && (
              <div className="invalid-feedback d-block">{validation.password.message}</div>
            )}
          </div>
        </Form.Group>
        
        {/* Display API errors with no background - just text */}
        {apiError && (
          <div className="text-danger mb-3 text-center" role="alert" aria-live="assertive">
            <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
            {apiError}
          </div>
        )}
        
        <Row className="flex-between-center mb-7">
          <Col xs="auto">
            <Form.Check type="checkbox" className="mb-0">
              <Form.Check.Input
                type="checkbox"
                name="remember-me"
                id="remember-me"
                defaultChecked
              />
              <Form.Check.Label htmlFor="remember-me" className="mb-0">
                {t("rememberMe")}
              </Form.Check.Label>
            </Form.Check>
          </Col>
          <Col xs="auto">
            <Link
              to="/auth/forgot-password"
              className="fs-9 fw-semibold"
            >
              {t("forgotPassword")}
            </Link>
          </Col>
        </Row>
        <Button 
          variant="warning" 
          type="submit" 
          className="w-100 mb-3" 
          disabled={!isFormValid || isSubmitting || loginLoading}
        >
          {isSubmitting || loginLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-1"
                style={{ width: '0.75rem', height: '0.75rem' }} // Smaller spinner
              />
              <span className="ms-1">{t("loading")}</span>
            </>
          ) : (
            t("signIn")
          )}
        </Button>
      </Form>
      <div className="text-center">
        <Link
          to="/auth/register"
          className="fs-9 fw-bold"
        >
          {t("createAnAccount")}
        </Link>
      </div>
    </div>
  );
};

export default SignInForm;
